import { Component, HostBinding, Input } from '@angular/core';
import { Router } from '@angular/router';
import { DateTime } from 'luxon';
import { isExplicitPlanSelectionBOrC } from 'src/app/pages/new-billing/utils/explicit-plan-selection.utils';
import { GrowthBookService } from 'src/app/services/growthbook/service';

export interface SubscriptionStatus {
  status: 'trial' | 'paid' | 'past_due' | 'canceled';
  remainingTrialDays?: number;
  cancellationRequest?: { requestDate?: string };
  nextPaymentDate?: string;
}

@Component({
  selector: 'app-subscription-status-badge',
  templateUrl: './subscription-status-badge.component.html',
  styleUrls: ['./subscription-status-badge.component.scss'],
})
export class SubscriptionStatusBadgeComponent {
  constructor(
    private readonly router: Router,
    private readonly growthBookService: GrowthBookService,
  ) {}

  @Input() subscription!: SubscriptionStatus;
  @Input() small = true;
  @Input() noTextMobile = true;
  /** When set (with trial status), overrides the default trial pill background. */
  @Input() pillBackgroundColor: string | null | undefined;
  /** Optional text color for the trial pill when using `pillBackgroundColor`. */
  @Input() pillTextColor: string | null | undefined;
  /**
   * When true (e.g. billing plan widget), always show "Trial - X days left" even when
   * explicit-plan-selection B/C would show "Current" on choose-plan surfaces.
   */
  @Input() preferTrialDaysLeft = false;
  @Input() trialLabelOnly = false;

  @HostBinding('class.dynamic-pill')
  get dynamicPillClass(): boolean {
    return (
      !!this.pillBackgroundColor &&
      this.subscription?.status === 'trial' &&
      !this.hasCancellationRequest
    );
  }

  @HostBinding('style.--pill-bg')
  get pillBgCssVar(): string | null {
    return this.dynamicPillClass && this.pillBackgroundColor
      ? this.pillBackgroundColor
      : null;
  }

  @HostBinding('style.--pill-fg')
  get pillFgCssVar(): string | null {
    if (!this.dynamicPillClass) {
      return null;
    }
    return this.pillTextColor ?? null;
  }

  get hasCancellationRequest(): boolean {
    return !!this.subscription?.cancellationRequest?.requestDate;
  }

  /** explicit-plan-selection B/C: trial pill shows "Current" instead of "Trial - X days left". */
  get showTrialStatusAsCurrent(): boolean {
    if (this.subscription?.status !== 'trial') {
      return false;
    }
    if (this.preferTrialDaysLeft) {
      return false;
    }
    const tdInternal = this.router.parseUrl(this.router.url).queryParams['td_internal'];
    return isExplicitPlanSelectionBOrC(this.growthBookService, tdInternal);
  }

  private formatPendingCancellationText(): string {
    const nextPayment = this.subscription?.nextPaymentDate;
    const formattedDate = this.formatDate(nextPayment);
    return formattedDate ? `Active until ${formattedDate}` : 'Active until';
  }

  private formatDate(dateIso?: string): string | null {
    if (!dateIso) return null;

    const dt = DateTime.fromISO(dateIso);
    if (!dt.isValid) return null;

    const now = DateTime.now();
    return dt.hasSame(now, 'year') ? dt.toFormat('MMM d') : dt.toFormat('MMM d, yyyy');
  }

  getBadgeText(): string {
    if (this.hasCancellationRequest) {
      return this.formatPendingCancellationText();
    }

    switch (this.subscription.status) {
      case 'trial':
        return this.subscription.status;
      case 'paid':
        return 'Active';
      case 'past_due':
        return 'billing.pastDue';
      case 'canceled':
        return 'billing.canceled';
      default:
        return this.subscription.status;
    }
  }

  shouldTranslate(): boolean {
    if (this.hasCancellationRequest) {
      return false;
    }

    return ['past_due', 'canceled'].includes(this.subscription.status);
  }

  getBadgeClass(): string {
    if (this.hasCancellationRequest) {
      return 'custom-badge-cancel-request';
    }

    return `custom-badge-${this.subscription.status || 'default'}`;
  }
}
